import { useCallback, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';

import { usePayrollCalendarDatesStore } from '@/modules/employer/components/SetupEmployer/Features/PayrollCalendar/stores';
const STEP = 6;
const MIN_MONTHS = 18;
export const groupDatesByMonth = (dates: string[], step: number, startDate?: string) => {
  // Empty dates: preserve previous behaviour (MIN_MONTHS span).
  if (dates.length === 0) {
    const monthCount = MIN_MONTHS - 1;

    return {
      monthCount,
      maxStep: Math.floor(monthCount / step),
      currentMonthPage: 0,
    };
  }

  // Base month = earliest of startDate (when before the first date) and the first date.
  const baseMonth =
    startDate && dayjs(startDate).isBefore(dayjs(dates[0]), 'month')
      ? dayjs(startDate).startOf('month')
      : dayjs(dates[0]).startOf('month');

  // Last month with a date — computed from the max date (do not rely on ordering).
  const lastMonth = dates
    .reduce((max, date) => (dayjs(date).isAfter(max) ? dayjs(date) : max), dayjs(dates[0]))
    .startOf('month');

  // Inclusive span of months from base to the last month carrying a date.
  const spanMonths = lastMonth.diff(baseMonth, 'month') + 1;

  const monthCount = (spanMonths < MIN_MONTHS ? MIN_MONTHS : spanMonths) - 1;
  const maxStep = Math.floor(monthCount / step);

  const today = dayjs().startOf('month');
  let currentMonthStep = 0;

  for (let i = 0; i <= maxStep; i++) {
    const startMonth = dayjs(dates[0])
      .startOf('month')
      .add(i * step, 'month');
    const endMonth = startMonth.add(step, 'month');

    if (today.isSame(startMonth) || (today.isAfter(startMonth) && today.isBefore(endMonth))) {
      currentMonthStep = i + 1;
      break;
    }
  }

  return {
    monthCount,
    maxStep,
    currentMonthPage: Math.max(0, currentMonthStep - 1),
  };
};

export const usePayrollCalendarDates = (editMode?: boolean) => {
  const [page, setPage] = useState(0);
  const dates = usePayrollCalendarDatesStore((state) => state.dates);
  const handleSetDates = usePayrollCalendarDatesStore((state) => state.handleSetDates);
  const initialMonthDate = usePayrollCalendarDatesStore((state) => state.initialMonthDate);
  const minDate = usePayrollCalendarDatesStore((state) => state.minDate);
  const endDate = usePayrollCalendarDatesStore((state) => state.endDate);

  const { monthCount, maxStep, currentMonthPage } = groupDatesByMonth(dates, STEP, initialMonthDate);

  const onPickDate = useCallback(
    (date: Dayjs) => {
      const preparedDate = date.format('YYYY-MM-DD') as string;
      if (dates.includes(preparedDate)) {
        handleSetDates(dates.filter((d) => d !== preparedDate));
      } else {
        const preparedSortedDates = [...dates, preparedDate].sort((a, b) => dayjs(a).diff(dayjs(b)));
        handleSetDates(preparedSortedDates);
      }
    },
    [dates, handleSetDates]
  );

  const onPickDates = useCallback(
    (newDates: Dayjs[]) => {
      const filteredDates = newDates.filter((date) => date.isValid());
      const preparedDates = filteredDates.map((date) => date.format('YYYY-MM-DD') as string);
      const datesToAdd = dates.filter((date) => !preparedDates.includes(date));
      const preparedSortedDates = [...datesToAdd, ...preparedDates].sort((a, b) => dayjs(a).diff(dayjs(b)));
      handleSetDates(preparedSortedDates);
    },
    [dates, handleSetDates]
  );

  useEffect(() => {
    if (currentMonthPage !== 0) {
      setPage(currentMonthPage);
    }
  }, [currentMonthPage]);

  useEffect(() => {
    setPage(editMode ? currentMonthPage : 0);
  }, [initialMonthDate, editMode, currentMonthPage]);

  return {
    dates,
    onPickDate,
    onPickDates,
    page,
    setPage,
    initialMonthDate,
    minDate,
    endDate,
    maxStep,
    monthCount,
  };
};
